/**
 * Vehicle Selector – core interaction logic
 * - Make/Model tab: Year → Make → Model → Trim → Engine cascade
 * - VIN tab: NHTSA DecodeVin & auto-fill
 * Depends on: vehicle-data.js (VehicleData.getMakes())
 */
(function () {
  'use strict';

  var STORAGE_KEY   = 'vs_vehicles';
  var ACTIVE_KEY    = 'vs_active_vehicle';
  var MAX_SAVED     = 5;
  var NHTSA_BASE    = 'https://vpic.nhtsa.dot.gov/api/vehicles';
  var FUEL_ECO_BASE = 'https://www.fueleconomy.gov/ws/rest/vehicle/menu';

  // ── Storage ───────────────────────────────────────────────────────────────
  function getSaved() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch (e) { return []; }
  }
  function setSaved(list) { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); }
  function getActive() {
    try { return JSON.parse(localStorage.getItem(ACTIVE_KEY)) || null; }
    catch (e) { return null; }
  }
  function setActive(v) {
    if (v) localStorage.setItem(ACTIVE_KEY, JSON.stringify(v));
    else   localStorage.removeItem(ACTIVE_KEY);
  }
  function vehicleLabel(v) {
    return [v.year, v.make, v.model, v.trim, v.engine].filter(Boolean).join(' ');
  }

  // ── Helpers ───────────────────────────────────────────────────────────────
  function generateYears() {
    var years = [];
    for (var y = new Date().getFullYear() + 1; y >= 1990; y--) years.push(String(y));
    return VehicleData.filterYears(years);
  }

  function populateMakes(sel, year) {
    var prev = sel.value;
    populateSelect(sel, VehicleData.getMakes(year), 'Make');
    if (prev) { sel.value = prev; syncDisplay(sel); }
  }

  function fetchModelNames(url) {
    return fetch(url)
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function (j) {
        var names = (j.Results || []).map(function (x) { return x.Model_Name; });
        return names.filter(function (v, i, a) { return a.indexOf(v) === i; }).sort();
      });
  }

  // Year-scoped list avoids impossible combos (e.g. 2014 Chrysler Daytona);
  // falls back to all models when NHTSA has no data for that year.
  function fetchModels(make, year) {
    var configured = VehicleData.configuredModels(make, year);
    if (configured) return Promise.resolve(configured);
    var all = NHTSA_BASE + '/GetModelsForMake/' + encodeURIComponent(make) + '?format=json';
    var request = !year ? fetchModelNames(all) :
      fetchModelNames(NHTSA_BASE + '/GetModelsForMakeYear/make/' + encodeURIComponent(make) +
          '/modelyear/' + encodeURIComponent(year) + '?format=json')
        .then(function (names) { return names.length ? names : fetchModelNames(all); });
    return request
      .catch(function (err) { if (VehicleData.filterModels(make, [], year).length) return []; throw err; })
      .then(function (names) { return VehicleData.filterModels(make, names, year); });
  }

  // fueleconomy.gov returns a bare object instead of an array when there is a single result.
  function menuItems(j) {
    var m = j && j.menuItem;
    return Array.isArray(m) ? m : (m ? [m] : []);
  }

  function fetchFuelEcoSubModels(year, make, nhtsaModel) {
    return fetch(FUEL_ECO_BASE + '/model?year=' + encodeURIComponent(year) + '&make=' + encodeURIComponent(make), {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function (j) {
        var norm = nhtsaModel.toLowerCase();
        var trims = menuItems(j).map(function (x) { return x.value; })
          .filter(function (v) { return v.toLowerCase().indexOf(norm) === 0; });
        return VehicleData.filterTrims(make, trims, year, nhtsaModel);
      });
  }

  function fetchFuelEcoEngines(year, make, subModel) {
    return fetch(FUEL_ECO_BASE + '/options?year=' + encodeURIComponent(year) + '&make=' + encodeURIComponent(make) + '&model=' + encodeURIComponent(subModel), {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function (j) {
        return menuItems(j).map(function (x) { return x.text; });
      });
  }

  // Trim is hidden on the storefront, so offer the engines of every (allowed) trim of the model.
  function fetchEnginesForModel(year, make, model) {
    var configured = VehicleData.configuredEngines(make, year, model);
    if (configured) return Promise.resolve(configured);
    return fetchFuelEcoSubModels(year, make, model)
      .catch(function () { return []; })
      .then(function (trims) {
        return Promise.all(trims.map(function (t) {
          return fetchFuelEcoEngines(year, make, t).catch(function () { return []; });
        }));
      })
      .then(function (lists) {
        var seen = {}, out = [];
        lists.forEach(function (list) {
          list.forEach(function (e) { if (!seen[e]) { seen[e] = true; out.push(e); } });
        });
        return VehicleData.filterEngines(make, out.sort(), year, model);
      });
  }

  // NHTSA engine/trim wording never matches the fueleconomy.gov options used as product tags,
  // and engine is part of the tag filter, so VIN results carry year/make/model only.
  function decodeVIN(vin) {
    return fetch(NHTSA_BASE + '/DecodeVinValues/' + encodeURIComponent(vin) + '?format=json')
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function (j) {
        var r = (j.Results || [])[0] || {};
        return { make: r.Make || '', model: r.Model || '', year: r.ModelYear || '', trim: '', engine: '' };
      });
  }

  function titleCase(str) {
    return str.toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  // Mirrors Shopify's handleize so plain product tags ("Audi", "A3", "2016") match the URL.
  function handleize(str) {
    return String(str || '').toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function buildSearchUrl(v) {
    var parts = [v.make, v.model, v.year, v.engine].map(handleize).filter(Boolean);
    return '/collections/all/' + parts.join('+');
  }

  // Read by collection-product-grid.liquid: Shopify redirects away unknown tags on the
  // primary domain, so the landing page can't see what was requested otherwise.
  function rememberRequestedFilter(url) {
    var m = String(url).match(/\/collections\/([^/?#]+)\/([^/?#]+)/);
    if (!m) return;
    try {
      sessionStorage.setItem('vs_requested_filter', JSON.stringify({ handle: m[1], tags: m[2], at: Date.now() }));
    } catch (e) {}
  }

  // ── DOM helpers ───────────────────────────────────────────────────────────
  var dom = {};

  function populateSelect(sel, options, placeholder) {
    sel.innerHTML = '';
    var ph = document.createElement('option');
    ph.value = ''; ph.disabled = true; ph.selected = true;
    ph.textContent = placeholder;
    sel.appendChild(ph);
    options.forEach(function (o) {
      var el = document.createElement('option');
      el.value = el.textContent = o;
      sel.appendChild(el);
    });
    sel.disabled = options.length === 0;
    syncDisplay(sel);
  }
  function resetSelect(sel, ph) { populateSelect(sel, [], ph); sel.disabled = true; }
  function loadingSelect(sel) {
    sel.innerHTML = '<option value="" disabled selected>Loading\u2026</option>';
    sel.disabled = true;
    syncDisplay(sel);
  }
  function syncDisplay(sel) {
    var wrap = sel && sel.parentElement;
    if (!wrap || !wrap.classList.contains('vsw__select-wrap')) return;
    var textEl = wrap.querySelector('.vsw__sel-text');
    var valSpan = wrap.querySelector('.vsw__sel-val');
    if (!valSpan) return;
    if (sel.value) {
      var idx = sel.selectedIndex;
      valSpan.textContent = (idx >= 0 && sel.options[idx]) ? sel.options[idx].text : sel.value;
      if (textEl) textEl.classList.add('has-value');
    } else {
      var phOpt = sel.options[0];
      valSpan.textContent = (phOpt && phOpt.text) ? phOpt.text : (wrap.getAttribute('data-placeholder') || '');
      if (textEl) textEl.classList.remove('has-value');
    }
  }

  // ── Tab switching ─────────────────────────────────────────────────────────
  function initTabs() {
    var tabs   = document.querySelectorAll('.vsw__tab');
    var panels = document.querySelectorAll('.vsw__panel');
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.dataset.tab;
        tabs.forEach(function (t) {
          t.classList.toggle('vsw__tab--active', t.dataset.tab === target);
          t.setAttribute('aria-selected', t.dataset.tab === target ? 'true' : 'false');
        });
        panels.forEach(function (p) {
          var active = p.id === 'vsw-panel-' + target;
          p.classList.toggle('vsw__panel--active', active);
          p.hidden = !active;
        });
      });
    });
  }

  // ── Make/Model cascade: Year → Make → Model → Trim → Engine ──────────────
  // Bumped on every cascade change so slower, superseded API responses are ignored.
  var cascadeSeq = 0;

  function onYearChange() {
    ++cascadeSeq;
    syncDisplay(dom.selYear);
    populateMakes(dom.selMake, dom.selYear.value);
    dom.selMake.disabled = !dom.selYear.value;
    if (dom.selMake.value) { onMakeChange(); return; }
    resetSelect(dom.selModel,  'Model');
    resetSelect(dom.selTrim,   'Trim');
    resetSelect(dom.selEngine, 'Engine');
    updateSaveBtn();
  }

  function onMakeChange() {
    var seq = ++cascadeSeq;
    syncDisplay(dom.selMake);
    loadingSelect(dom.selModel);
    resetSelect(dom.selTrim,   'Trim');
    resetSelect(dom.selEngine, 'Engine');
    updateSaveBtn();
    var make = dom.selMake.value;
    if (!make) { resetSelect(dom.selModel, 'Model'); return; }
    fetchModels(make, dom.selYear.value)
      .then(function (models) {
        if (seq !== cascadeSeq) return;
        populateSelect(dom.selModel, models, 'Model');
      })
      .catch(function () { if (seq === cascadeSeq) resetSelect(dom.selModel, 'Error – retry'); });
  }

  function onModelChange() {
    var seq = ++cascadeSeq;
    syncDisplay(dom.selModel);
    resetSelect(dom.selTrim,   'Trim');
    resetSelect(dom.selEngine, 'Engine');
    updateSaveBtn();
    var year  = dom.selYear.value;
    var make  = dom.selMake.value;
    var model = dom.selModel.value;
    if (!year || !make || !model) return;
    loadingSelect(dom.selEngine);
    fetchEnginesForModel(year, make, model)
      .then(function (engines) {
        if (seq !== cascadeSeq) return;
        if (engines.length > 0) {
          populateSelect(dom.selEngine, engines, 'Engine');
        } else {
          resetSelect(dom.selEngine, 'Engine (optional)');
          dom.selEngine.disabled = false;
        }
      })
      .catch(function () {
        if (seq !== cascadeSeq) return;
        resetSelect(dom.selEngine, 'Engine (optional)');
        dom.selEngine.disabled = false;
      });
  }

  function onTrimChange() {
    var seq = ++cascadeSeq;
    syncDisplay(dom.selTrim);
    resetSelect(dom.selEngine, 'Engine');
    updateSaveBtn();
    var year = dom.selYear.value;
    var make = dom.selMake.value;
    var trim = dom.selTrim.value;
    if (!year || !make || !trim) return;
    loadingSelect(dom.selEngine);
    fetchFuelEcoEngines(year, make, trim)
      .then(function (engines) {
        if (seq !== cascadeSeq) return;
        if (engines.length > 0) {
          populateSelect(dom.selEngine, engines, 'Engine');
        } else {
          resetSelect(dom.selEngine, 'Engine (optional)');
          dom.selEngine.disabled = false;
        }
      })
      .catch(function () {
        if (seq !== cascadeSeq) return;
        resetSelect(dom.selEngine, 'Engine (optional)');
        dom.selEngine.disabled = false;
      });
  }

  function updateSaveBtn() {
    // button is always enabled; validation happens on click
  }

  // ── Save / Clear ──────────────────────────────────────────────────────────
  /**
   * @param {object} v         Vehicle object
   * @param {boolean} redirect Whether to navigate to search page after save
   */
  function saveVehicle(v, redirect) {
    if (!v.year || !v.make || !v.model) return;
    var saved = getSaved();
    var label = vehicleLabel(v);
    saved = saved.filter(function (s) { return vehicleLabel(s) !== label; });
    saved.unshift(v);
    if (saved.length > MAX_SAVED) saved = saved.slice(0, MAX_SAVED);
    setSaved(saved);
    setActive(v);
    updateSelectedBar();
    document.dispatchEvent(new CustomEvent('vehicleSelected', { detail: v }));
    if (redirect) {
      var url = buildSearchUrl(v);
      rememberRequestedFilter(url);
      window.location.href = url;
    } else {
      showToast('Vehicle saved: ' + label);
    }
  }

  function onSaveClick() {
    if (!dom.selYear.value || !dom.selMake.value || !dom.selModel.value) {
      showToast('Please select Year, Make and Model first');
      dom.btnSave.classList.add('vsw--shake');
      setTimeout(function () { dom.btnSave.classList.remove('vsw--shake'); }, 500);
      return;
    }
    saveVehicle({
      year:   dom.selYear.value,
      make:   dom.selMake.value,
      model:  dom.selModel.value,
      trim:   dom.selTrim   ? (dom.selTrim.value   || '') : '',
      engine: dom.selEngine ? (dom.selEngine.value || '') : ''
    }, true /* redirect to search results */);
  }

  function clearVehicle() {
    setActive(null);
    populateSelect(dom.selYear, generateYears(), 'Year');
    dom.selMake.disabled = true;
    populateSelect(dom.selMake, VehicleData.getMakes(), 'Make');
    resetSelect(dom.selModel,  'Model');
    resetSelect(dom.selTrim,   'Trim');
    resetSelect(dom.selEngine, 'Engine');
    updateSaveBtn();
    updateSelectedBar();
    // Update header trigger button
    var valEl = document.getElementById('vs-trigger-value');
    var lblEl = document.getElementById('vs-trigger-label');
    if (valEl) { valEl.textContent = 'Add Your Vehicle'; }
    if (lblEl) { lblEl.textContent = ''; }
    showToast('Vehicle selection cleared');
    document.dispatchEvent(new CustomEvent('vehicleCleared'));
  }

  function onChangeVehicle() {
    if (dom.selectedBar) dom.selectedBar.style.display = 'none';
    var tabMM = document.getElementById('vsw-tab-mm');
    if (tabMM) tabMM.click();
    var active = getActive();
    if (active) prefillForm(active);
  }

  // ── Pre-fill form ─────────────────────────────────────────────────────────
  function prefillForm(v) {
    if (!dom.selYear) return; // bar not present on this page
    populateSelect(dom.selYear, generateYears(), 'Year');
    if (v.year) { dom.selYear.value = v.year; syncDisplay(dom.selYear); }
    populateSelect(dom.selMake, VehicleData.getMakes(v.year), 'Make');
    dom.selMake.disabled = !v.year;
    if (v.make) { dom.selMake.value = v.make; syncDisplay(dom.selMake); }
    resetSelect(dom.selModel,  'Model');
    resetSelect(dom.selTrim,   'Trim');
    resetSelect(dom.selEngine, 'Engine');
    if (dom.selMake.value) {
      var seq = ++cascadeSeq;
      var stale = function () { return seq !== cascadeSeq; };
      loadingSelect(dom.selModel);
      fetchModels(v.make, v.year)
        .then(function (models) {
          if (stale()) return null;
          populateSelect(dom.selModel, models, 'Model');
          if (v.model) { dom.selModel.value = v.model; dom.selModel.disabled = false; syncDisplay(dom.selModel); }
          updateSaveBtn();
          if (!dom.selModel.value || !v.year) return Promise.resolve(null);
          loadingSelect(dom.selEngine);
          return fetchEnginesForModel(v.year, v.make, v.model);
        })
        .then(function (engines) {
          if (engines === null || stale()) return;
          if (engines.length === 0) {
            resetSelect(dom.selEngine, 'Engine (optional)');
            dom.selEngine.disabled = false;
            return;
          }
          populateSelect(dom.selEngine, engines, 'Engine');
          if (v.engine) { dom.selEngine.value = v.engine; syncDisplay(dom.selEngine); }
          updateSaveBtn();
        })
        .catch(function () { updateSaveBtn(); });
    } else {
      updateSaveBtn();
    }
  }

  // ── VIN Tab ───────────────────────────────────────────────────────────────
  function initVIN() {
    var input  = document.getElementById('vs-vin-input');
    var btnDec = document.getElementById('vs-btn-vin-decode');
    var status = document.getElementById('vs-vin-status');
    if (!input || !btnDec) return;

    input.addEventListener('input', function () {
      var val = input.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 17);
      input.value = val;
      var valid = val.length === 17;
      input.classList.toggle('is-valid',   valid);
      input.classList.toggle('is-invalid', val.length > 0 && !valid);
      if (status) status.textContent = valid ? '\u2713' : (val.length > 0 ? '\u2717' : '');
      btnDec.disabled = !valid;
    });

    btnDec.addEventListener('click', function () {
      var vin = input.value.trim().toUpperCase();
      if (vin.length !== 17) return;
      btnDec.disabled = true;
      btnDec.textContent = 'Decoding\u2026';
      if (status) status.textContent = '\u27f3';

      decodeVIN(vin)
        .then(function (v) {
          if (!v.make || !v.model || !v.year) {
            showToast('Could not decode VIN – please check and retry');
            if (status) status.textContent = '\u2717';
            btnDec.disabled = false;
            btnDec.textContent = 'SAVE';
            return;
          }
          v.make  = titleCase(v.make);
          v.model = titleCase(v.model);
          var allowedMake = VehicleData.matchVehicle(v);
          if (!allowedMake) {
            showToast('Sorry, ' + vehicleLabel(v) + ' is not supported yet');
            if (status) status.textContent = '\u2717';
            btnDec.disabled = false;
            btnDec.textContent = 'SAVE';
            return;
          }
          v.make = allowedMake;
          saveVehicle(v, true /* redirect */);
        })
        .catch(function () {
          showToast('Network error – please try again');
          if (status) status.textContent = '\u2717';
          btnDec.disabled = false;
          btnDec.textContent = 'SAVE';
        });
    });
  }

  // ── Selected vehicle bar (homepage) ──────────────────────────────────────
  function updateSelectedBar() {
    var active = getActive();
    if (!dom.selectedBar) return;
    if (active) {
      dom.selectedBar.style.display = 'flex';
      var lbl = document.getElementById('vs-selected-label');
      if (lbl) lbl.textContent = vehicleLabel(active);
    } else {
      dom.selectedBar.style.display = 'none';
    }
  }

  // ── Toast ─────────────────────────────────────────────────────────────────
  var _toastTimer;
  function showToast(msg) {
    var t = document.getElementById('vs-toast');
    if (!t) return;
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(_toastTimer);
    _toastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 2500);
  }

  // ── Modal Inline Add-Vehicle Form (works on all pages) ───────────────────
  function initModalForm() {
    var modal    = document.getElementById('vs-modal');
    var mYear    = document.getElementById('vsm-mm-year');
    var mMake    = document.getElementById('vsm-mm-make');
    var mModel   = document.getElementById('vsm-mm-model');
    var mTrim    = document.getElementById('vsm-mm-trim');
    var mEngine  = document.getElementById('vsm-mm-engine');
    var mSave    = document.getElementById('vsm-mm-save');
    if (!mYear) return;

    var mFormReady = false;
    function ensureFormPopulated() {
      if (mFormReady) return;
      mFormReady = true;
      populateSelect(mYear, generateYears(), 'Year');
      populateSelect(mMake, VehicleData.getMakes(), 'Make');
      mMake.disabled = true;
    }

    // Lazy-populate on first modal open
    document.getElementById('vs-trigger-btn').addEventListener('click', ensureFormPopulated, { once: false });

    function mUpdateSave() {
      // button is always enabled; validation happens on click
    }

    mYear.addEventListener('change', function () {
      populateMakes(mMake, mYear.value);
      mMake.disabled = !mYear.value;
      if (mMake.value) { mMake.dispatchEvent(new Event('change')); return; }
      ++mSeq;
      resetSelect(mModel, 'Model');
      if (mTrim)   { resetSelect(mTrim,   'Trim');   mTrim.disabled   = true; }
      if (mEngine) { resetSelect(mEngine, 'Engine'); mEngine.disabled = true; }
      mUpdateSave();
    });

    var mSeq = 0;

    mMake.addEventListener('change', function () {
      var seq = ++mSeq;
      loadingSelect(mModel);
      if (mTrim)   { resetSelect(mTrim,   'Trim');   mTrim.disabled   = true; }
      if (mEngine) { resetSelect(mEngine, 'Engine'); mEngine.disabled = true; }
      mUpdateSave();
      var make = mMake.value;
      if (!make) { resetSelect(mModel, 'Model'); return; }
      fetchModels(make, mYear.value)
        .then(function (models) { if (seq === mSeq) populateSelect(mModel, models, 'Model'); })
        .catch(function () { if (seq === mSeq) resetSelect(mModel, 'Error – retry'); });
    });

    mModel.addEventListener('change', function () {
      var seq = ++mSeq;
      if (mTrim)   { resetSelect(mTrim,   'Trim');   mTrim.disabled   = true; }
      if (mEngine) { resetSelect(mEngine, 'Engine'); mEngine.disabled = true; }
      mUpdateSave();
      var year = mYear.value; var make = mMake.value; var model = mModel.value;
      if (!model || !mEngine) return;
      loadingSelect(mEngine);
      fetchEnginesForModel(year, make, model)
        .then(function (engines) {
          if (seq !== mSeq) return;
          if (engines && engines.length) {
            populateSelect(mEngine, engines, 'Engine');
          } else {
            resetSelect(mEngine, 'Engine (optional)');
          }
          mEngine.disabled = false;
        })
        .catch(function () { if (seq === mSeq) { resetSelect(mEngine, 'Engine (optional)'); mEngine.disabled = false; } });
    });

    if (mTrim) {
      mTrim.addEventListener('change', function () {
        var seq = ++mSeq;
        if (mEngine) { resetSelect(mEngine, 'Engine'); mEngine.disabled = true; }
        var year = mYear.value; var make = mMake.value; var trim = mTrim.value;
        if (!trim) return;
        loadingSelect(mEngine);
        fetchFuelEcoEngines(year, make, trim)
          .then(function (engines) {
            if (seq !== mSeq) return;
            if (engines && engines.length) {
              populateSelect(mEngine, engines, 'Engine');
            } else {
              resetSelect(mEngine, 'Engine (optional)');
            }
            mEngine.disabled = false;
          })
          .catch(function () { if (seq === mSeq) { resetSelect(mEngine, 'Engine (optional)'); mEngine.disabled = false; } });
      });
    }

    mSave.addEventListener('click', function () {
      if (!mYear.value || !mMake.value || !mModel.value) {
        showToast('Please select Year, Make and Model first');
        mSave.classList.add('vsw--shake');
        setTimeout(function () { mSave.classList.remove('vsw--shake'); }, 500);
        return;
      }
      var v = {
        year:   mYear.value,
        make:   mMake.value,
        model:  mModel.value,
        trim:   mTrim   ? (mTrim.value   || '') : '',
        engine: mEngine ? (mEngine.value || '') : ''
      };
      saveVehicle(v, false);
      // Update header trigger button
      var valEl = document.getElementById('vs-trigger-value');
      var lblEl = document.getElementById('vs-trigger-label');
      if (valEl) {
        if (lblEl) lblEl.textContent = 'Your vehicle';
        valEl.textContent = [v.year, v.make, v.model].filter(Boolean).join(' ');
      }
      // Reset form
      populateSelect(mYear, generateYears(), 'Year');
      populateSelect(mMake, VehicleData.getMakes(), 'Make');
      mMake.disabled = true;
      resetSelect(mModel, 'Model');
      if (mTrim)   { resetSelect(mTrim,   'Trim');   mTrim.disabled   = true; }
      if (mEngine) { resetSelect(mEngine, 'Engine'); mEngine.disabled = true; }
      // Close modal
      if (modal) {
        modal.setAttribute('hidden', '');
        modal.classList.remove('is-open');
        var overlay = document.getElementById('vs-overlay');
        if (overlay) { overlay.classList.remove('is-active'); overlay.setAttribute('aria-hidden', 'true'); }
        document.body.style.overflow = '';
      }
    });

    // Modal-form tab switching
    var formTabs   = document.querySelectorAll('[data-vsm-tab]');
    var formPanels = { mm: document.getElementById('vsm-panel-mm'), vin: document.getElementById('vsm-panel-vin') };
    formTabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.getAttribute('data-vsm-tab');
        formTabs.forEach(function (t) {
          var active = t.getAttribute('data-vsm-tab') === target;
          t.classList.toggle('vsm__form-tab--active', active);
          t.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        Object.keys(formPanels).forEach(function (key) {
          var p = formPanels[key];
          if (!p) return;
          var active = key === target;
          p.classList.toggle('vsm__form-panel--active', active);
          p.hidden = !active;
        });
      });
    });

    // VIN decode in modal
    var vinInput  = document.getElementById('vsm-vin-input');
    var vinStatus = document.getElementById('vsm-vin-status');
    var vinSave   = document.getElementById('vsm-vin-save');
    if (vinInput && vinSave) {
      vinInput.addEventListener('input', function () {
        var val = vinInput.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 17);
        vinInput.value = val;
        var valid = val.length === 17;
        vinInput.classList.toggle('is-valid',   valid);
        vinInput.classList.toggle('is-invalid', val.length > 0 && !valid);
        if (vinStatus) vinStatus.textContent = valid ? '\u2713' : (val.length > 0 ? '\u2717' : '');
        vinSave.disabled = !valid;
      });

      vinSave.addEventListener('click', function () {
        var vin = vinInput.value.trim().toUpperCase();
        if (vin.length !== 17) return;
        vinSave.disabled = true;
        if (vinStatus) vinStatus.textContent = 'Decoding\u2026';
        fetch('https://vpic.nhtsa.dot.gov/api/vehicles/decodevin/' + vin + '?format=json')
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var results = data.Results || [];
            function getVal(v) {
              var r = results.find(function (x) { return x.Variable === v; });
              return r && r.Value && r.Value !== 'Not Applicable' ? r.Value : '';
            }
            var year  = getVal('Model Year');
            var make  = getVal('Make');
            var model = getVal('Model');
            if (!year || !make || !model) {
              if (vinStatus) vinStatus.textContent = '\u2717 Not found';
              vinSave.disabled = false;
              return;
            }
            var v = { year: year, make: make, model: model, trim: '', engine: '' };
            var allowedMake = VehicleData.matchVehicle(v);
            if (!allowedMake) {
              if (vinStatus) vinStatus.textContent = '\u2717 Not supported';
              showToast('Sorry, ' + [year, make, model].join(' ') + ' is not supported yet');
              vinSave.disabled = false;
              return;
            }
            v.make = allowedMake;
            if (vinStatus) vinStatus.textContent = '\u2713';
            saveVehicle(v, false);
            // Update header trigger button
            var valEl = document.getElementById('vs-trigger-value');
            var lblEl = document.getElementById('vs-trigger-label');
            if (valEl) {
              if (lblEl) lblEl.textContent = 'Your vehicle';
              valEl.textContent = [v.year, v.make, v.model].filter(Boolean).join(' ');
            }
            vinInput.value = '';
            vinStatus.textContent = '';
            vinSave.disabled = true;
            if (modal) {
              modal.setAttribute('hidden', '');
              modal.classList.remove('is-open');
              var overlay = document.getElementById('vs-overlay');
              if (overlay) { overlay.classList.remove('is-active'); overlay.setAttribute('aria-hidden', 'true'); }
              document.body.style.overflow = '';
            }
          })
          .catch(function () {
            if (vinStatus) vinStatus.textContent = '\u2717 Error';
            vinSave.disabled = false;
          });
      });
    }
  }

  // ── Header Vehicle Management Modal ──────────────────────────────────────
  function initModal() {
    var modal     = document.getElementById('vs-modal');
    var overlay   = document.getElementById('vs-overlay');
    var triggerBtn = document.getElementById('vs-trigger-btn');
    if (!modal) return;

    var carImgUrl = modal.getAttribute('data-car-img') || '';

    var EDIT_SVG  = '<svg width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M15.805 1.71a.679.679 0 0 0-.66.185l-.005.005-8.03 8.03a.679.679 0 1 0 .96.96l8.036-8.036a.68.68 0 0 0-.3-1.144ZM3.882 1.695A2.194 2.194 0 0 0 1.687 3.89v10.228c0 1.212.983 2.194 2.195 2.194H14.11a2.194 2.194 0 0 0 2.194-2.194v-6.25a.679.679 0 1 0-1.358 0v6.25a.836.836 0 0 1-.836.836H3.882a.836.836 0 0 1-.836-.836V3.89c0-.462.374-.837.836-.837h6.061a.679.679 0 1 0 0-1.358H3.882Z" fill="#262728" opacity="0.75"></path></svg>';
    var TRASH_SVG = '<svg width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.71 1.137c0 .353.28.638.625.638h3.334a.631.631 0 0 0 .625-.637.631.631 0 0 0-.625-.638H7.335a.631.631 0 0 0-.625.637ZM2.127 3.9h.833v11.262c0 1.291 1.026 2.338 2.292 2.338h7.5c1.266 0 2.292-1.047 2.292-2.338v-9.35a.631.631 0 0 0-.625-.637.631.631 0 0 0-.625.637v9.35c0 .282-.11.552-.306.752-.195.199-.46.31-.736.31h-7.5c-.276 0-.541-.111-.737-.31-.195-.2-.305-.47-.305-.752V3.9h11.667a.631.631 0 0 0 .625-.638.631.631 0 0 0-.625-.637H2.127a.631.631 0 0 0-.625.637c0 .352.28.638.625.638ZM6.71 7.512c0-.352.28-.637.625-.637s.625.285.625.637v5.1c0 .352-.28.638-.625.638a.631.631 0 0 1-.625-.638v-5.1Zm3.959-.637a.631.631 0 0 0-.625.637v5.1c0 .352.28.638.625.638a.631.631 0 0 0 .625-.638v-5.1a.631.631 0 0 0-.625-.637Z" fill="#262728" opacity="0.75"></path></svg>';

    function escHtml(s) {
      return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function openModal() {
      renderModal();
      modal.removeAttribute('hidden');
      modal.classList.add('is-open');
      if (overlay) { overlay.classList.add('is-active'); overlay.setAttribute('aria-hidden', 'false'); }
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      modal.setAttribute('hidden', '');
      modal.classList.remove('is-open');
      if (overlay) { overlay.classList.remove('is-active'); overlay.setAttribute('aria-hidden', 'true'); }
      document.body.style.overflow = '';
    }

    function buildCarCard(v, idx, isActive) {
      var name = [(v.year || ''), (v.make || '')].filter(Boolean).join(' ');
      var sub  = [v.model, v.trim, v.engine].filter(Boolean).join(' / ');
      return '<div class="vsm__vehicle-card' + (isActive ? ' vsm__vehicle-card--active' : '') + '">' +
        '<img src="' + escHtml(carImgUrl) + '" class="vsm__car-img" alt="">' +
        '<div class="vsm__car-info">' +
          '<p class="vsm__car-name">' + escHtml(name) + '</p>' +
          '<p class="vsm__car-sub">' + escHtml(sub) + '</p>' +
        '</div>' +
        '<div class="vsm__car-actions">' +
          '<button class="vsm__car-btn" data-action="edit" data-idx="' + idx + '" aria-label="Edit vehicle">' + EDIT_SVG + '</button>' +
          '<button class="vsm__car-btn" data-action="delete" data-idx="' + idx + '" aria-label="Delete vehicle">' + TRASH_SVG + '</button>' +
        '</div>' +
      '</div>';
    }

    function buildSavedItem(v, idx) {
      var name = [(v.year || ''), (v.make || '')].filter(Boolean).join(' ');
      var sub  = [v.model, v.trim, v.engine].filter(Boolean).join(' / ');
      return '<li class="vsm__saved-item" data-saved-idx="' + idx + '">' +
        '<img src="' + escHtml(carImgUrl) + '" class="vsm__saved-img" alt="">' +
        '<div class="vsm__saved-info">' +
          '<p class="vsm__saved-name">' + escHtml(name) + '</p>' +
          '<p class="vsm__saved-sub">' + escHtml(sub) + '</p>' +
        '</div>' +
      '</li>';
    }

    function renderModal() {
      var saved    = getSaved();
      var active   = getActive();
      var bodyEl   = document.getElementById('vsm-body');
      var emptyEl  = document.getElementById('vsm-empty');
      var curEl    = document.getElementById('vsm-current-card');
      var listEl   = document.getElementById('vsm-saved-list');

      if (!saved || saved.length === 0) {
        if (bodyEl)  bodyEl.setAttribute('hidden', '');
        if (emptyEl) emptyEl.removeAttribute('hidden');
        updateTriggerBtn(null);
        return;
      }

      if (bodyEl)  bodyEl.removeAttribute('hidden');
      if (emptyEl) emptyEl.setAttribute('hidden', '');

      // Find active index
      var activeLabel = active ? vehicleLabel(active) : null;
      var activeIdx = 0;
      if (activeLabel) {
        saved.forEach(function (s, i) { if (vehicleLabel(s) === activeLabel) activeIdx = i; });
      }

      if (curEl)  curEl.innerHTML  = buildCarCard(saved[activeIdx], activeIdx, true);

      if (listEl) {
        var html = '';
        saved.forEach(function (v, i) {
          if (i === activeIdx) return;
          html += buildSavedItem(v, i);
        });
        listEl.innerHTML = html || '<li class="vsm__saved-item" style="cursor:default;border-color:#f0f0f0;color:#bbb;font-size:13px">No other saved vehicles</li>';
      }

      updateTriggerBtn(active);
    }

    function updateTriggerBtn(v) {
      var valEl = document.getElementById('vs-trigger-value');
      var lblEl = document.getElementById('vs-trigger-label');
      if (!valEl) return;
      if (v) {
        if (lblEl) lblEl.textContent = 'Your vehicle';
        valEl.textContent = [(v.year || ''), (v.make || ''), (v.model || '')].filter(Boolean).join(' ');
      } else {
        if (lblEl) lblEl.textContent = '';
        valEl.textContent = 'Add Your Vehicle';
      }
    }

    // Delegated click handler for modal body
    modal.addEventListener('click', function (e) {
      var editBtn   = e.target.closest('[data-action="edit"]');
      var deleteBtn = e.target.closest('[data-action="delete"]');
      var savedItem = e.target.closest('.vsm__saved-item');

      if (editBtn) {
        e.stopPropagation();
        var idx = parseInt(editBtn.getAttribute('data-idx'), 10);
        var v = getSaved()[idx];
        if (!v) return;
        setActive(v);
        updateTriggerBtn(v);
        closeModal();
        var bar = document.getElementById('vehicle-fitment-bar');
        if (bar) {
          bar.scrollIntoView({ behavior: 'smooth', block: 'center' });
          prefillForm(v);
        } else {
          window.location.href = '/#vehicle-fitment-bar';
        }
        return;
      }

      if (deleteBtn) {
        e.stopPropagation();
        var idx = parseInt(deleteBtn.getAttribute('data-idx'), 10);
        var saved = getSaved();
        var removed = saved.splice(idx, 1)[0];
        setSaved(saved);
        if (removed && getActive() && vehicleLabel(getActive()) === vehicleLabel(removed)) {
          setActive(saved.length ? saved[0] : null);
          document.dispatchEvent(new CustomEvent('vehicleCleared'));
        }
        updateSelectedBar();
        renderModal();
        updateTriggerBtn(getActive());
        return;
      }

      if (savedItem && !e.target.closest('.vsm__car-btn')) {
        var idx = parseInt(savedItem.getAttribute('data-saved-idx'), 10);
        var v = getSaved()[idx];
        if (!v) return;
        setActive(v);
        updateTriggerBtn(v);
        updateSelectedBar();
        document.dispatchEvent(new CustomEvent('vehicleSelected', { detail: v }));
        closeModal();
        return;
      }
    });

    // Open/close bindings
    if (triggerBtn) triggerBtn.addEventListener('click', openModal);
    if (overlay)    overlay.addEventListener('click', closeModal);
    var closeBtn = document.getElementById('vs-modal-close');
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });

    // Footer buttons
    var shopWithout = document.getElementById('vsm-btn-shop-without');
    if (shopWithout) {
      shopWithout.addEventListener('click', function () {
        setActive(null);
        updateSelectedBar();
        updateTriggerBtn(null);
        document.dispatchEvent(new CustomEvent('vehicleCleared'));
        closeModal();
      });
    }

    var addBtn = document.getElementById('vsm-btn-add');
    if (addBtn) {
      addBtn.addEventListener('click', function () {
        closeModal();
        var bar = document.getElementById('vehicle-fitment-bar');
        if (bar) {
          bar.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          window.location.href = '/';
        }
      });
    }

    // Set initial trigger button text
    updateTriggerBtn(getActive());
  }

  // ── Init ──────────────────────────────────────────────────────────────────
  function init() {
    dom.selYear    = document.getElementById('vs-year');
    dom.selMake    = document.getElementById('vs-make');
    dom.selModel   = document.getElementById('vs-model');
    dom.selTrim    = document.getElementById('vs-trim');
    dom.selEngine  = document.getElementById('vs-engine');
    dom.btnSave    = document.getElementById('vs-btn-save');
    dom.btnClear   = document.getElementById('vs-btn-clear');
    dom.btnChange  = document.getElementById('vs-btn-change');
    dom.selectedBar = document.getElementById('vs-selected-bar');

    // Bar-specific init (homepage only)
    if (dom.selYear) {
      populateSelect(dom.selYear, generateYears(), 'Year');
      populateSelect(dom.selMake, VehicleData.getMakes(), 'Make');
      dom.selMake.disabled = true;

      dom.selYear.addEventListener('change',  onYearChange);
      dom.selMake.addEventListener('change',  onMakeChange);
      dom.selModel.addEventListener('change', onModelChange);
      if (dom.selTrim) dom.selTrim.addEventListener('change', onTrimChange);
      if (dom.selEngine) dom.selEngine.addEventListener('change', function () { syncDisplay(dom.selEngine); });

      dom.btnSave.addEventListener('click', onSaveClick);
      if (dom.btnClear)  dom.btnClear.addEventListener('click',  clearVehicle);
      if (dom.btnChange) dom.btnChange.addEventListener('click', onChangeVehicle);

      initTabs();
      initVIN();

      var active = getActive();
      if (active) prefillForm(active);
      updateSelectedBar();
    }

    // Modal init (all pages)
    initModal();
    initModalForm();
  }

  // Scripts are defer — by the time they run, full DOM is ready.
  init();

  // ── Public API ────────────────────────────────────────────────────────────
  window.VehicleSelector = {
    getActive: getActive,
    getSaved:  getSaved,
    onSelect:  function (fn) { document.addEventListener('vehicleSelected', function (e) { fn(e.detail); }); },
    onClear:   function (fn) { document.addEventListener('vehicleCleared', fn); }
  };

})();

